import { useState } from 'react';
import { createRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Check, ExternalLink } from 'lucide-react';
import { Route as rootRoute } from './__root';
import { plansQueryOptions } from '../lib/queries/onboarding';
import { assignmentsQueryOptions } from '../lib/queries/assignments';
import Modal from '../components/Modal';
import NewPlanForm from '../components/plans/NewPlanForm';
import { STATUS_STYLE } from '../lib/status';
import { requireOperator } from '../lib/guards';

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/plans',
  beforeLoad: requireOperator,
  component: PlansPage,
});

// Derive plan-tier features from name — replace with DB data when schema supports it
const PLAN_FEATURES: Record<string, string[]> = {
  Starter: ['Email support'],
  Growth: ['Priority support + Slack'],
  Enterprise: ['Dedicated CSM + SLA'],
};

type Plan = {
  id: string;
  name: string;
  includedTokens: number;
  overageRate: string;
  platformAccessFee: string;
  maxUsers: number;
  tenantCount: number;
};

function PlanCard({ plan }: { plan: Plan }) {
  const isCustom = plan.platformAccessFee === '0.00';
  const isUnlimitedUsers = plan.maxUsers === 0;
  const extraFeatures = PLAN_FEATURES[plan.name] ?? [];

  const features = [
    !isCustom && plan.includedTokens > 0
      ? `${(plan.includedTokens / 1000).toFixed(0)}K tokens / period`
      : 'Custom token allowance',
    isCustom
      ? 'Negotiated overage rate'
      : `$${plan.overageRate} / token overage`,
    isUnlimitedUsers ? 'Unlimited users' : `Up to ${plan.maxUsers} users`,
    ...extraFeatures,
  ].filter(Boolean) as string[];

  return (
    <Modal>
      <div className="bg-bg-elevated flex flex-col rounded-lg border border-white/8 p-5">
        {/* Desktop-only: name + edit at top */}
        <div className="mb-4 hidden items-start justify-between md:flex">
          <span className="text-primary font-semibold">{plan.name}</span>
          <Modal.Open opens={`edit-form-${plan.id}`}>
            <button className="text-muted hover:text-primary rounded border border-white/10 px-3 py-1 text-xs transition-colors">
              Edit
            </button>
          </Modal.Open>
        </div>

        {/* Mobile: two-col row | Desktop: single-col stack */}
        <div className="flex flex-row justify-between md:flex-col md:gap-8">
          {/* Left col (mobile) / price block (desktop) */}
          <div className="flex flex-col justify-start gap-4 md:justify-start">
            {/* Mobile-only name above price */}
            <span className="text-primary font-semibold md:hidden">
              {plan.name}
            </span>
            <p
              className={[
                'text-primary font-bold',
                isCustom ? 'text-4xl' : 'text-3xl',
              ].join(' ')}
            >
              {isCustom
                ? 'Custom'
                : `$${Number(plan.platformAccessFee).toFixed(0)}`}
            </p>
          </div>

          {/* Features list */}
          <ul className="mb-0 space-y-2 md:mb-6">
            {features.map((f) => (
              <li
                key={f}
                className="text-subtle flex items-center gap-2 text-sm"
              >
                <Check size={13} className="text-muted shrink-0" />
                {f}
              </li>
            ))}
          </ul>
        </div>

        {/* Bottom bar: tenant count + mobile Edit button */}
        <div className="mt-3 flex items-center justify-between border-t border-white/8 pt-3">
          <span className="text-muted text-[10px] font-semibold tracking-widest uppercase">
            {`${plan.tenantCount} tenant${plan.tenantCount !== 1 ? 's' : ''} on this plan`}
          </span>
          {/* Mobile-only edit button */}
          <div className="md:hidden">
            <Modal.Open opens={`edit-form-${plan.id}`}>
              <button className="text-primary hover:text-primary rounded border border-white/10 px-3 py-1 text-xs transition-colors">
                Edit
              </button>
            </Modal.Open>
          </div>
        </div>
      </div>

      <Modal.Window name={`edit-form-${plan.id}`}>
        <NewPlanForm plan={plan} />
      </Modal.Window>
    </Modal>
  );
}

function PlansPage() {
  const { data: plans = [], isLoading } = useQuery(plansQueryOptions());

  const [page, setPage] = useState(1);
  const { data: assignmentsData } = useQuery(assignmentsQueryOptions(page));
  const assignments = assignmentsData?.assignments ?? [];

  return (
    <div className="space-y-8 p-6">
      {/* Plan cards */}
      {isLoading ? (
        <div className="grid grid-cols-3 gap-4">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="bg-bg-elevated h-64 animate-pulse rounded-lg border border-white/8"
            />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          {plans.map((p) => (
            <PlanCard key={p.id} plan={p} />
          ))}
        </div>
      )}

      {/* Tenant assignments Desktop */}
      <div className="hidden md:block">
        <h2 className="text-primary mb-4 font-semibold">Tenants assignments</h2>
        <div className="border-primary/8 overflow-hidden rounded-lg border">
          <table className="bg-bg-elevated w-full text-sm">
            <thead>
              <tr className="border-primary/8 border-b">
                <th className="text-subtle px-4 py-3 text-left text-[0.625rem] uppercase">
                  Tenant
                </th>
                <th className="text-subtle px-4 py-3 text-left text-[0.625rem] uppercase">
                  Plan
                </th>

                <th className="text-subtle px-4 py-3 text-left text-[0.625rem] uppercase">
                  Allowance
                </th>

                <th className="text-subtle px-4 py-3 text-left text-[0.625rem] uppercase">
                  Overage Rate
                </th>

                <th className="text-subtle hidden px-4 py-3 text-left text-[0.625rem] uppercase lg:table-cell">
                  Assigned by
                </th>
              </tr>
            </thead>
            <tbody>
              {assignments.length === 0 ? (
                <tr>
                  <td
                    colSpan={6}
                    className="text-muted px-4 py-8 text-center text-sm"
                  >
                    No tenant assignments yet
                  </td>
                </tr>
              ) : (
                assignments.map((a) => (
                  <tr
                    key={a.id}
                    className="border-primary/8 border-b last:border-0"
                  >
                    <td className="text-primary px-4 py-5">{a.id}</td>
                    <td className="px-4 py-3">
                      <span className="text-muted rounded border border-white/20 px-2 py-0.5 text-xs">
                        {a.planName}
                      </span>
                    </td>
                    <td className="text-muted px-4 py-3">{a.includedTokens}</td>
                    <td className="text-muted px-4 py-3">{a.overageRate}</td>
                    <td className="text-muted hidden px-4 py-3 lg:table-cell">
                      {'assignedBy'}
                    </td>
                    <td className="px-4 py-3">
                      <button className="text-accent flex items-center gap-1 text-xs hover:underline">
                        <ExternalLink size={11} /> Stripe
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Tenant assignments Mobile  */}
      <div className="block md:hidden">
        <h2 className="text-muted mb-4 text-xs">Tenants assignments</h2>
        {assignments.length === 0 ? (
          <p className="text-muted text-xs">No tenants yet</p>
        ) : (
          <ul className="flex flex-col gap-3">
            {assignments.map((assignment) => (
              <li
                key={assignment.id}
                className="bg-bg-elevated flex w-full items-center justify-between rounded p-4"
              >
                <div className="flex items-center gap-6">
                  <p className="text-primary text-xs font-medium">
                    {assignment.id.slice(0, 12) + ' ...'}
                  </p>
                  <span
                    className={[
                      'rounded-full border px-2 py-0.5 text-[0.625rem] capitalize',
                      STATUS_STYLE[assignment.status] ??
                        'text-muted border-white/20',
                    ].join(' ')}
                  >
                    {assignment.status}
                  </span>
                </div>
                <p className="text-subtle text-[0.625rem] uppercase">
                  {assignment.planName}
                </p>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
